// Pure helpers for working with form schemas, field references, and calculation
// expressions. Shared by FormBuilder (editing) and FormFields (rendering).
import * as math from 'mathjs';
import { normalizeHeader } from './formHeader';
import { flattenFields, isSection, normalizeEntries, sectionFields } from './formSections';

// A calculation stores its references to other questions as the referenced
// field's id wrapped in double braces: "{{field_1712}} / 100". The braces keep
// a reference atomic, so a question titled "Observation/Sight score" can never
// be split into identifiers and a division by the expression parser. Everything
// outside the braces is plain arithmetic typed by the author.
const FIELD_REF_SOURCE = '\\{\\{\\s*([^{}]+?)\\s*\\}\\}';
const fieldRefPattern = () => new RegExp(FIELD_REF_SOURCE, 'g');

// Identifiers as they appeared in pre-reference expressions ("sight_score * 2").
const IDENTIFIER_SOURCE = '[A-Za-z_][A-Za-z0-9_]*';
const isIdentifier = (value) => new RegExp(`^${IDENTIFIER_SOURCE}$`).test(value);

// Characters an author may type directly into an expression. Field references
// are only ever added as whole tokens, so letters are deliberately excluded:
// nothing a user types can turn into a variable name.
const TYPEABLE_CHARS = /[^0-9.+\-*/()%\s]/g;

// What a field is called in reports, exports, and calculation boxes: the short
// field name when the author gave one, otherwise the question text itself.
export const getFieldDisplayName = (field) =>
  (field?.name || '').trim() || (field?.label || '').trim() || field?.id || '';

export const makeFieldRef = (fieldOrId) =>
  `{{${typeof fieldOrId === 'string' ? fieldOrId : fieldOrId.id}}}`;

// Strip anything that isn't arithmetic from text typed into the expression box.
export const sanitizeExpressionInput = (text) => (text || '').replace(TYPEABLE_CHARS, '');

// Run `transform` over the parts of an expression that sit *outside* field
// references, leaving the references themselves untouched.
const mapPlainSegments = (expression, transform) => {
  const pattern = fieldRefPattern();
  let result = '';
  let lastIndex = 0;
  let match = pattern.exec(expression);
  while (match !== null) {
    result += transform(expression.slice(lastIndex, match.index)) + match[0];
    lastIndex = match.index + match[0].length;
    match = pattern.exec(expression);
  }
  return result + transform(expression.slice(lastIndex));
};

// Field names/ids/labels that older expressions may still mention by bare name.
const legacyTokenMap = (fields) => {
  const map = new Map();
  fields.forEach((field) => {
    [field.name, field.id, field.label].forEach((candidate) => {
      const token = typeof candidate === 'string' ? candidate.trim() : '';
      if (token && isIdentifier(token) && !map.has(token)) {
        map.set(token, field.id);
      }
    });
  });
  return map;
};

// Rewrite an expression written before references existed ("sight_score / 2")
// into the braced form. Expressions already using braces pass through unchanged.
export const migrateExpression = (expression, fields = []) => {
  if (!expression) return '';
  const legacy = legacyTokenMap(fields);
  if (legacy.size === 0) return expression;
  const identifiers = new RegExp(IDENTIFIER_SOURCE, 'g');
  return mapPlainSegments(expression, (segment) =>
    segment.replace(identifiers, (identifier) =>
      (legacy.has(identifier) ? makeFieldRef(legacy.get(identifier)) : identifier)),
  );
};

// Split an expression into the pieces the editor draws: field references become
// boxes, everything else stays as typed operator/number text.
export const tokenizeExpression = (expression, fields = []) => {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const tokens = [];
  const pushText = (value) => {
    if (value) tokens.push({ type: 'text', value });
  };
  const pattern = fieldRefPattern();
  const source = expression || '';
  let lastIndex = 0;
  let match = pattern.exec(source);
  while (match !== null) {
    pushText(source.slice(lastIndex, match.index));
    tokens.push({ type: 'field', id: match[1], field: byId.get(match[1]) || null });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(source);
  }
  pushText(source.slice(lastIndex));
  return tokens;
};

// Backspace for the expression box: drops a whole field reference when the
// expression ends with one, otherwise a single typed character.
export const removeLastToken = (expression) => {
  if (!expression) return '';
  const trailingRef = new RegExp(`${FIELD_REF_SOURCE}$`).exec(expression);
  if (trailingRef) return expression.slice(0, trailingRef.index);
  return expression.slice(0, -1);
};

export const getReferencedFieldIds = (expression, fields = []) => {
  const pattern = fieldRefPattern();
  const source = migrateExpression(expression || '', fields);
  const ids = [];
  let match = pattern.exec(source);
  while (match !== null) {
    const id = match[1].trim();
    if (!ids.includes(id)) ids.push(id);
    match = pattern.exec(source);
  }
  return ids;
};

export const getReferencedFields = (expression, fields = []) => {
  const byId = new Map(fields.map((field) => [field.id, field]));
  return getReferencedFieldIds(expression, fields)
    .map((id) => byId.get(id))
    .filter(Boolean);
};

// References to questions that have since been deleted from the form.
export const getUnknownFieldRefs = (expression, fields = []) => {
  const known = new Set(fields.map((field) => field.id));
  return getReferencedFieldIds(expression, fields).filter((id) => !known.has(id));
};

// Turn a stored expression into something mathjs can parse: each reference
// becomes a generated variable, so no author-entered text reaches the parser.
const compileExpression = (expression, fields) => {
  const varByFieldId = new Map();
  const code = migrateExpression(expression, fields).replace(fieldRefPattern(), (_match, rawId) => {
    const id = rawId.trim();
    if (!varByFieldId.has(id)) varByFieldId.set(id, `_ref${varByFieldId.size}`);
    return varByFieldId.get(id);
  });
  return { code, varByFieldId };
};

const runExpression = (expression, fields, valuesByFieldId) => {
  const { code, varByFieldId } = compileExpression(expression, fields);
  const scope = {};
  varByFieldId.forEach((variable, fieldId) => {
    const numeric = parseFloat(valuesByFieldId[fieldId]);
    // Unanswered references stay unbound, so mathjs reports an undefined symbol
    // and the calculation renders blank rather than a number built from gaps.
    if (!Number.isNaN(numeric)) scope[variable] = numeric;
  });
  try {
    const result = math.evaluate(code, scope);
    if (typeof result !== 'number' || !Number.isFinite(result)) {
      return { ok: false, error: 'Expression does not evaluate to a number' };
    }
    return { ok: true, value: result };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

// Evaluate a calculation against answered values, keyed by field id. Returns
// null when the expression is empty, incomplete, or invalid.
export const evaluateExpression = (expression, fields = [], valuesByFieldId = {}) => {
  if (!expression || !expression.trim()) return null;
  const outcome = runExpression(expression, fields, valuesByFieldId);
  return outcome.ok ? outcome.value : null;
};

// Bring a stored schema up to the shape the app expects: legacy expressions are
// rewritten to braced references, sections are given their full shape at every
// depth, and the pieces added after a form was first saved (report header,
// saved dropdown presets) get their defaults. Field names are left alone: a
// name is optional, and a missing one falls back to the question text.
export const normalizeSchema = (rawSchema) => {
  const schema = rawSchema && typeof rawSchema === 'object' ? rawSchema : {};
  const entries = normalizeEntries(schema.fields);
  // Calculations reference questions by id from anywhere on the form, so a
  // migration reads the questions as one list, sections or not.
  const fields = flattenFields(entries);
  const migrate = (field) =>
    (field.type === 'calculation' && field.expression
      ? { ...field, expression: migrateExpression(field.expression, fields) }
      : field);
  // Sections nest, so the walk does too: a calculation is migrated wherever it
  // was filed.
  const migrateEntries = (list) => list.map((entry) => (isSection(entry)
    ? { ...entry, fields: migrateEntries(sectionFields(entry)) }
    : migrate(entry)));
  return {
    ...schema,
    header: normalizeHeader(schema.header),
    optionPresets: Array.isArray(schema.optionPresets) ? schema.optionPresets : [],
    hiddenPresetIds: Array.isArray(schema.hiddenPresetIds) ? schema.hiddenPresetIds : [],
    fields: migrateEntries(entries),
  };
};

export const getAvailableFieldsForCalculation = (fields, currentFieldId) =>
  fields.filter((f) => (f.type === 'number' || f.type === 'calculation') && f.id !== currentFieldId);

// Dry-run a calculation with every referenced question set to 1.
export const testCalculation = (expression, fields = []) => {
  if (!expression || !expression.trim()) return { valid: false, error: 'Expression is empty' };
  if (getUnknownFieldRefs(expression, fields).length > 0) {
    return {
      valid: false,
      error: 'This calculation refers to a question that no longer exists. Remove the highlighted box and add a current one.',
    };
  }
  const sampleValues = {};
  getReferencedFieldIds(expression, fields).forEach((id) => {
    sampleValues[id] = 1;
  });
  const outcome = runExpression(expression, fields, sampleValues);
  return outcome.ok ? { valid: true, result: outcome.value } : { valid: false, error: outcome.error };
};

export const detectCircularDependency = (fields, currentFieldId, expression) => {
  const calcIds = new Set(fields.filter((f) => f.type === 'calculation').map((f) => f.id));
  const dependenciesOf = (expr) =>
    getReferencedFieldIds(expr, fields).filter((id) => calcIds.has(id));

  const graph = new Map();
  fields
    .filter((f) => f.type === 'calculation')
    .forEach((f) => {
      graph.set(f.id, dependenciesOf(f.id === currentFieldId ? expression : f.expression || ''));
    });
  if (!graph.has(currentFieldId)) graph.set(currentFieldId, dependenciesOf(expression));

  const visited = new Set();
  const inProgress = new Set();

  const hasCycle = (node) => {
    if (inProgress.has(node)) return true;
    if (visited.has(node)) return false;
    visited.add(node);
    inProgress.add(node);
    const cyclic = (graph.get(node) || []).some(hasCycle);
    inProgress.delete(node);
    return cyclic;
  };

  return Array.from(graph.keys()).some(hasCycle);
};

// Fields referenced by a calculation that don't yet have a usable value (used to
// show a "Waiting for: ..." hint while filling a form).
export const getMissingFields = (expression, schemaFields = [], formData = {}) => {
  if (!expression) return [];
  const byId = new Map(schemaFields.map((field) => [field.id, field]));
  const missing = [];
  getReferencedFieldIds(expression, schemaFields).forEach((id) => {
    const field = byId.get(id);
    if (!field) return;
    if (Number.isNaN(parseFloat(formData[field.id]))) {
      missing.push(getFieldDisplayName(field));
    }
  });
  return missing;
};
